/**
 * Shell router (DESIGN-client §7): the store-driven game screens (boot → tutorial|lobby → table
 * with handEnd/matchEnd overlays) at the game's address — `/play` on the web, `/` inside Discord
 * (`paths.ts`). The public pages (/rules, /privacy, /terms, /discord) are the static site now.
 * `?screen=<name>` overrides under `?e2e=1`; `?smoke=1` shows the scaffold smoke panel.
 */
import { useEffect, useState } from 'react';
import { installFirstGestureUnlock } from '../audio/install';
import { authNoticeText } from '../bridge/identity';
import { cueQueue } from '../net/cues';
import { type Screen, store, useStore } from '../store/store';
import { installDefaultAnnouncerVoice } from './announcerManifest';
import { useScreenTitle } from './meta';
import { ErrorBoundary } from './ErrorBoundary';
import { ConnectionBanner } from './overlays/ConnectionBanner';
import { RotateHint } from './overlays/RotateHint';
import { gameHome, isGamePath } from './paths';
import { installRouting } from './routing';
import { e2eParam } from './runtime';
import { Boot } from './screens/Boot';
import { HandEnd } from './screens/HandEnd';
import { Lobby } from './screens/Lobby';
import { MatchEnd } from './screens/MatchEnd';
import { TableScreen, preloadScene } from './screens/TableScreen';
import { TutorialGate } from './screens/TutorialGate';
import { NamePickHost } from './sheets/PickName';
import { SmokePanel } from './SmokePanel';
import { ToastHost, toast } from './ui';

const SCREENS: readonly Screen[] = ['boot', 'lobby', 'table', 'handEnd', 'matchEnd', 'tutorial'];
const UI_ATLAS_URL = '/assets/atlases/ui-panel@2x.png';

let routingInstalled = false;

/** Publish `settings.motion` to the document so CSS can honour it (see theme.css). */
function useMotionAttribute(): void {
  const motion = useStore((s) => s.settings.motion);
  useEffect(() => {
    const root = document.documentElement;
    if (motion === 'system') delete root.dataset['motion'];
    else root.dataset['motion'] = motion;
  }, [motion]);
}

/**
 * Say what a sign-in link or the Discord callback did, once. Boot leaves the outcome in the store
 * (`session.authNotice`); it is cleared as soon as it is shown so a remount cannot repeat it.
 */
function useAuthNotice(): void {
  const notice = useStore((s) => s.session.authNotice);
  useEffect(() => {
    if (!notice) return;
    const { text, kind } = authNoticeText(notice);
    toast(text, kind, 5200);
    store.getState().setSession({ authNotice: null });
  }, [notice]);
}

function useShellSetup(): void {
  useEffect(() => {
    if (!routingInstalled) {
      routingInstalled = true;
      installRouting(store, cueQueue);
    }
    const override = e2eParam('screen');
    if (override && (SCREENS as readonly string[]).includes(override)) store.getState().setPresentation({ screen: override as Screen });

    // Announcer voice pack, if one has been generated and committed. Fire and forget: a missing
    // pack is the normal state and leaves the announcer subtitle-only.
    void installDefaultAnnouncerVoice();

    // Audio unlocks on the first gesture anywhere, not on a particular button. Boot no longer has
    // a "Deal me in" tap to carry it, and the lobby bed plays long before any table builds the
    // sfx install that used to be the only other caller. Detaches itself once the engine is up.
    const offGesture = installFirstGestureUnlock();

    // Warm the scene chunk now that the shell has painted (C-T25). Deferred to an idle callback so
    // it competes with nothing on the boot path; by the time anyone leaves the lobby it is cached.
    const warm = () => void preloadScene();
    const idle = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
    const warmId = idle ? idle(warm, { timeout: 3000 }) : window.setTimeout(warm, 1200);

    // Chunky 9-slice panels only once the ui atlas actually exists (else the CSS fallback).
    const img = new Image();
    img.onload = () => {
      document.documentElement.dataset['uiAtlas'] = '1';
      document.documentElement.style.setProperty('--panel-image', `url("${UI_ATLAS_URL}")`);
    };
    img.src = UI_ATLAS_URL;

    return () => {
      offGesture();
      const cancelIdle = (window as unknown as { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback;
      if (idle && cancelIdle) cancelIdle(warmId);
      else window.clearTimeout(warmId);
    };
  }, []);
}

function GameScreens() {
  const screen = useStore((s) => s.presentation.screen);
  const code = useStore((s) => s.table?.code ?? null);
  const handNo = useStore((s) => s.table?.handNo ?? null);
  useScreenTitle(screen, { code, handNo });

  // The table stays mounted across table / handEnd / matchEnd.
  //
  // Returning `<TableScreen />` for one and a fragment for the others gave React a different
  // element type at every hand boundary, so it unmounted and remounted TableScreen and PixiHost:
  // the canvas detached and re-attached mid-cue (a felt-coloured flash at every hand end) and
  // `buildTableScene` ran again each time. Keeping one element type keeps one scene.
  if (screen === 'table' || screen === 'handEnd' || screen === 'matchEnd') {
    return (
      <>
        <TableScreen />
        {screen === 'handEnd' && <HandEnd />}
        {screen === 'matchEnd' && <MatchEnd />}
      </>
    );
  }
  switch (screen) {
    case 'boot':
      return <Boot />;
    case 'tutorial':
      return <TutorialGate />;
    case 'lobby':
      return <Lobby />;
  }
}

export function Shell() {
  useShellSetup();
  useMotionAttribute();
  useAuthNotice();
  const [path] = useState(() => location.pathname.replace(/\/+$/, '') || '/');
  const smoke = new URLSearchParams(location.search).get('smoke') === '1';

  // Any other path that reached the game document: rewrite the address to the game's own, keeping
  // the query (a `?join=` must survive) and say what happened if it was a link to nothing. The
  // server only serves this document at the game's paths, so this is mostly the dev server and the
  // server's no-site fallback, where the game answers at `/` on the web too; that one is not news.
  useEffect(() => {
    if (smoke || isGamePath(path)) return;
    try {
      history.replaceState(null, '', `${gameHome()}${location.search}${location.hash}`);
    } catch {
      /* history blocked; the game still renders */
    }
    if (path !== '/') toast(`Nothing at ${path} — here's the table instead`, 'info', 3600);
  }, [path, smoke]);

  const body: React.ReactNode = smoke ? <SmokePanel /> : <GameScreens />;

  return (
    <div className="shell" data-testid="shell">
      <ErrorBoundary>{body}</ErrorBoundary>
      <ConnectionBanner />
      <ToastHost />
      {/* A first email sign-in picks a name once the lobby is up. Renders nothing otherwise. */}
      {!smoke && <NamePickHost />}
      {/* Above everything, including the table: at phone-landscape sizes the scene lays two seats
          on top of each other, so there is nothing worth showing underneath. */}
      <RotateHint />
    </div>
  );
}
